import * as React from 'react'
import {
  ChevronRight as CaretRightIcon,
  ListOrdered as ListOrderedIcon,
  Users as UsersIcon,
  Workflow as FlowArrowIcon
} from 'lucide-react'
import type { NodeLabel } from '@atbash-labs/ontology-core'
import type { PlantNodeHrefContext } from '@atbash-labs/plantview-links'
import { usePlantSessionPaint } from '@axilon-apps/domains-ontology-ui-state'
import { cn } from '../../lib/utils'
import { PlantHref } from './PlantHref'
import { equipmentIconFor } from './equipment-icon'
import {
  SessionMarkAffordances,
  SessionRollupPill,
  countSessionMarks,
  sessionMarkClass
} from './session-mark-ui'

type IconComponent = React.ComponentType<{ size?: number | string; className?: string }>

export type FlowTileKind = 'line' | 'equipment' | 'labor' | 'operation'

/** How the primary name behaves when the tile width is constrained. */
export type FlowTileNameLayout = 'truncate' | 'wrap'

const DEFAULT_ICON: Record<FlowTileKind, IconComponent> = {
  line: FlowArrowIcon,
  equipment: equipmentIconFor(undefined),
  labor: UsersIcon,
  operation: ListOrderedIcon
}

/**
 * Shared pill used by SublineFlow and the Station work graph.
 * Kind only picks the default icon; callers pass the PlantHref label /
 * node context so navigation stays on the unified plantHref path.
 */
export function FlowTile({
  kind,
  id,
  label,
  node,
  name,
  subtitle,
  detail,
  icon,
  equipmentType,
  memberIds,
  nameLayout = 'truncate'
}: {
  kind: FlowTileKind
  id: string
  label: NodeLabel
  node?: PlantNodeHrefContext
  name: string
  subtitle?: string
  /** Optional relationship context shown inside the node. */
  detail?: string
  icon?: IconComponent
  /** When `kind` is equipment, prefer the type-aware icon. */
  equipmentType?: string
  /** Live child ids (e.g. equipment under a step) for +/~/− without containment rollups. */
  memberIds?: readonly string[]
  /**
   * DAG canvases opt into `'wrap'` so typical plant names stay readable inside a
   * fixed node box; linear flows keep the compact one-line default.
   */
  nameLayout?: FlowTileNameLayout
}): React.ReactElement {
  const Icon = icon ?? (kind === 'equipment' ? equipmentIconFor(equipmentType) : DEFAULT_ICON[kind])
  const { markFor, rollupByContainerId, touchedRollupByContainerId, deletedRollupByContainerId } =
    usePlantSessionPaint()
  const mark = markFor(id)
  const rollup =
    memberIds !== undefined
      ? countSessionMarks(memberIds, markFor, 'added')
      : (rollupByContainerId.get(id) ?? 0)
  const touchedRollup =
    memberIds !== undefined
      ? countSessionMarks(memberIds, markFor, 'touched')
      : (touchedRollupByContainerId.get(id) ?? 0)
  const deletedRollup = deletedRollupByContainerId.get(id) ?? 0
  const wrapName = nameLayout === 'wrap'

  return (
    <PlantHref
      id={id}
      label={label}
      node={node}
      className={sessionMarkClass(
        cn(
          'inline-flex items-center gap-2 rounded-lg border bg-card text-left no-underline transition-colors hover:bg-accent hover:text-accent-foreground',
          wrapName ? 'px-2.5 py-1.5' : 'px-3 py-2'
        ),
        mark
      )}
    >
      <span
        className={cn(
          'grid shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3',
          wrapName ? 'size-5' : 'size-6'
        )}
      >
        <Icon size={wrapName ? 12 : 14} />
      </span>
      <span className="min-w-0 flex-1 overflow-hidden">
        <span className={cn('flex min-w-0 gap-1.5', wrapName ? 'items-start' : 'items-center')}>
          <span
            title={name}
            className={cn(
              'min-w-0 flex-1 font-semibold text-ink',
              wrapName ? 'line-clamp-2 break-words text-12 leading-snug' : 'block truncate text-13'
            )}
          >
            {name}
          </span>
          <SessionMarkAffordances mark={mark} />
          <SessionRollupPill count={rollup} modified={touchedRollup} deleted={deletedRollup} />
        </span>
        {subtitle !== undefined && subtitle !== '' && (
          <span className={cn('block truncate text-ink-3', wrapName ? 'text-10' : 'text-11')}>
            {subtitle}
          </span>
        )}
        {detail !== undefined && detail !== '' && (
          <span className="mt-1 block text-11 font-medium text-sky-700 dark:text-sky-300">
            {detail}
          </span>
        )}
      </span>
    </PlantHref>
  )
}

/** Caret separator between ordered flow tiles. */
export function FlowTileCaret(): React.ReactElement {
  return <CaretRightIcon size={14} className="text-ink-3" aria-hidden />
}
